import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Robot,
  Plus,
  Trash,
  ChatCircle,
  CheckSquare,
  ListBullets,
  Star,
  CircleNotch,
  Image as ImageIcon,
  X,
  CaretLeft,
  CheckCircle,
  Power,
  LinkSimple,
} from "@phosphor-icons/react";

const NODE_TYPES_META = {
  message: { icon: ChatCircle, label: "Message", color: "#10B981" },
  buttons: { icon: CheckSquare, label: "Buttons", color: "#3B82F6" },
  list: { icon: ListBullets, label: "List", color: "#8B5CF6" },
  cta_url: { icon: LinkSimple, label: "Link Button", color: "#0EA5E9" },
};

function uid(prefix = "n") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

// ---------- Custom node renderers ----------
function NodeShell({ id, data, selected, isStart, color, icon: Icon, children }) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 min-w-[220px] max-w-[280px] ${
        selected ? "border-[#25D366]" : "border-[#E5E7EB]"
      }`}
    >
      <Handle type="target" position={Position.Left} style={{ background: "#9CA3AF" }} />
      <div
        className="px-3 py-1.5 rounded-t-md flex items-center justify-between gap-2 text-white text-[11px] font-semibold"
        style={{ background: color }}
      >
        <span className="flex items-center gap-1.5">
          <Icon size={12} weight="fill" />
          {NODE_TYPES_META[data.type]?.label || data.type}
        </span>
        {isStart && (
          <span className="flex items-center gap-1 text-[10px] bg-white/25 px-1.5 py-0.5 rounded">
            <Star size={10} weight="fill" /> START
          </span>
        )}
      </div>
      <div className="p-2 text-xs text-[#111827]">{children}</div>
    </div>
  );
}

function MessageNode({ id, data, selected }) {
  return (
    <NodeShell id={id} data={{ ...data, type: "message" }} selected={selected} isStart={data.is_start} color="#10B981" icon={ChatCircle}>
      {data.media_id && (
        <div className="mb-1.5 flex items-center gap-1 text-[10px] text-[#6B7280]">
          <ImageIcon size={11} /> {data.media_kind || "media"}
        </div>
      )}
      <div className="whitespace-pre-wrap line-clamp-4 leading-snug">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <Handle type="source" position={Position.Right} style={{ background: "#10B981" }} />
    </NodeShell>
  );
}

function ButtonsNode({ id, data, selected }) {
  const buttons = (data.buttons || []).slice(0, 3);
  return (
    <NodeShell id={id} data={{ ...data, type: "buttons" }} selected={selected} isStart={data.is_start} color="#3B82F6" icon={CheckSquare}>
      <div className="whitespace-pre-wrap line-clamp-3 leading-snug mb-2">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <div className="space-y-1">
        {buttons.length === 0 ? (
          <div className="text-[10px] text-[#9CA3AF] italic">Add buttons →</div>
        ) : (
          buttons.map((b, i) => (
            <div key={b.id || i} className="relative bg-[#F3F4F6] rounded px-2 py-1 text-[11px] flex items-center justify-between">
              <span className="truncate">{b.text || "Button"}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={b.id}
                style={{
                  background: "#3B82F6", width: 10, height: 10,
                  right: -16, top: "50%", transform: "translateY(-50%)",
                }}
              />
            </div>
          ))
        )}
      </div>
    </NodeShell>
  );
}

function ListNode({ id, data, selected }) {
  const items = (data.list_items || []).slice(0, 10);
  return (
    <NodeShell id={id} data={{ ...data, type: "list" }} selected={selected} isStart={data.is_start} color="#8B5CF6" icon={ListBullets}>
      <div className="whitespace-pre-wrap line-clamp-3 leading-snug mb-2">{data.text || <em className="text-[#9CA3AF]">No text</em>}</div>
      <div className="space-y-1">
        {items.length === 0 ? (
          <div className="text-[10px] text-[#9CA3AF] italic">Add list items →</div>
        ) : (
          items.map((it, i) => (
            <div key={it.id || i} className="relative bg-[#F3F4F6] rounded px-2 py-1 text-[11px] flex items-center justify-between">
              <span className="truncate">{it.text || "Item"}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={it.id}
                style={{
                  background: "#8B5CF6", width: 10, height: 10,
                  right: -16, top: "50%", transform: "translateY(-50%)",
                }}
              />
            </div>
          ))
        )}
      </div>
    </NodeShell>
  );
}

// CTA URL node — renders a single tap-to-open-website button. Per WhatsApp
// `interactive.cta_url` spec, the customer's click opens the URL in their
// browser; there is no callback into the bot, so this node has only a
// TARGET handle (incoming) — no source handle for further routing.
function CTAUrlNode({ id, data, selected }) {
  return (
    <NodeShell id={id} data={{ ...data, type: "cta_url" }} selected={selected} isStart={data.is_start} color="#0EA5E9" icon={LinkSimple}>
      {data.media_id && (
        <div className="mb-1.5 flex items-center gap-1 text-[10px] text-[#6B7280]">
          <ImageIcon size={11} /> {data.media_kind || "media"}
        </div>
      )}
      <div className="whitespace-pre-wrap line-clamp-3 leading-snug mb-2">
        {data.text || <em className="text-[#9CA3AF]">No body text</em>}
      </div>
      <div className="rounded bg-[#0EA5E9]/10 border border-[#0EA5E9]/40 px-2 py-1.5 text-[11px] flex items-center gap-1.5 text-[#0369A1] font-semibold">
        <LinkSimple size={11} weight="bold" />
        <span className="truncate">{data.cta_label || "Visit"}</span>
      </div>
      {data.cta_url && (
        <div className="mt-1 text-[9px] text-[#6B7280] truncate font-mono" title={data.cta_url}>
          {data.cta_url}
        </div>
      )}
    </NodeShell>
  );
}

function TemplateTriggerNode({ data }) {
  // Read-only virtual node showing the template preview with one connectable
  // handle per QUICK_REPLY button. Edges from these handles map directly to
  // trigger_button_routes (handled in BotEditor).
  const { template_name, header, body, buttons = [] } = data || {};
  return (
    <div
      style={{ width: 280 }}
      className="bg-[#FFFBEB] rounded-lg shadow-sm border-2 border-[#FBBF24]"
    >
      <div className="px-3 py-1.5 rounded-t-md flex items-center justify-between gap-2 text-white text-[11px] font-semibold bg-[#F59E0B]">
        <span className="flex items-center gap-1.5">
          <Star size={12} weight="fill" /> Template Trigger
        </span>
        <span className="text-[10px] bg-white/25 px-1.5 py-0.5 rounded font-mono">
          {template_name}
        </span>
      </div>
      <div className="p-2.5 text-xs text-[#1F2937]">
        {header && <div className="font-semibold mb-1 text-[11px]">{header}</div>}
        {body && (
          <div className="whitespace-pre-wrap leading-snug bg-white border border-[#FDE68A] rounded p-1.5 text-[11px] mb-2 line-clamp-5">
            {body}
          </div>
        )}
        {buttons.length === 0 ? (
          <div className="text-[10px] text-[#9CA3AF] italic">No quick-reply buttons in this template.</div>
        ) : (
          <>
            <div className="text-[9px] uppercase tracking-wide text-[#92400E] font-semibold mb-1">
              Drag from a button → to a node
            </div>
            <div className="space-y-1">
              {buttons.map((b) => (
                <div key={b.text} className="relative bg-[#FEF3C7] rounded px-2 py-1 text-[11px] flex items-center justify-between border border-[#FBBF24]/40">
                  <span className="truncate">{b.text}</span>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`tpl::${b.text}`}
                    style={{
                      background: "#F59E0B", width: 12, height: 12,
                      right: -16, top: "50%", transform: "translateY(-50%)",
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const nodeTypes = {
  message: MessageNode,
  buttons: ButtonsNode,
  list: ListNode,
  cta_url: CTAUrlNode,
  template_trigger: TemplateTriggerNode,
};
const edgeTypes = {};

// ---------- Right panel node editor ----------
function NodeEditor({ node, onChange, onDelete, onSetStart, onClose }) {
  if (!node) return null;
  const data = node.data || {};
  const setData = (patch) => onChange({ ...node, data: { ...data, ...patch } });

  const updateButton = (idx, patch) => {
    const arr = [...(data.buttons || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setData({ buttons: arr });
  };
  const addButton = () => {
    const arr = [...(data.buttons || [])];
    if (arr.length >= 3) return toast.error("Max 3 buttons allowed");
    arr.push({ id: uid("btn"), text: "Option" });
    setData({ buttons: arr });
  };
  const delButton = (idx) => {
    const arr = [...(data.buttons || [])];
    arr.splice(idx, 1);
    setData({ buttons: arr });
  };

  const updateItem = (idx, patch) => {
    const arr = [...(data.list_items || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setData({ list_items: arr });
  };
  const addItem = () => {
    const arr = [...(data.list_items || [])];
    if (arr.length >= 10) return toast.error("Max 10 items allowed");
    arr.push({ id: uid("li"), text: "Item", description: "" });
    setData({ list_items: arr });
  };
  const delItem = (idx) => {
    const arr = [...(data.list_items || [])];
    arr.splice(idx, 1);
    setData({ list_items: arr });
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        throw new Error(typeof d === "string" ? d : d?.message || "Upload failed");
      }
      const d = await res.json();
      const kind = file.type.startsWith("image/") ? "image"
        : file.type.startsWith("video/") ? "video"
        : file.type.startsWith("audio/") ? "audio"
        : "document";
      setData({ media_id: d.media_id, media_kind: kind, media_filename: file.name });
      toast.success("Media uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="node-editor">
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--muted)]">Edit Node</div>
          <div className="text-sm font-semibold heading capitalize">{NODE_TYPES_META[node.type]?.label || node.type}</div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin px-4 py-3 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Message text</Label>
          <textarea
            value={data.text || ""}
            onChange={(e) => setData({ text: e.target.value })}
            placeholder="Type the message body…"
            rows={4}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-[var(--border)] bg-[var(--bg)] resize-none"
            data-testid="node-text"
          />
          <div className="text-[10px] text-[var(--muted)]">Supports *bold* and _italic_</div>
        </div>

        {/* Media (message + button header + cta_url header) */}
        {(node.type === "message" || node.type === "buttons" || node.type === "cta_url") && (
          <div className="space-y-1.5">
            <Label className="text-xs">Media (optional)</Label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*,video/*,application/pdf"
                id={`media-${node.id}`}
                className="hidden"
                onChange={(e) => uploadMedia(e.target.files?.[0])}
                data-testid="node-media-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={() => document.getElementById(`media-${node.id}`)?.click()}
              >
                <ImageIcon size={12} className="mr-1.5" />
                {data.media_id ? "Replace" : "Upload"}
              </Button>
              {data.media_id && (
                <>
                  <span className="text-[10px] text-[var(--muted)] truncate flex-1">
                    {data.media_filename || data.media_kind}
                  </span>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setData({ media_id: null, media_kind: null, media_filename: null })}
                  >
                    <X size={12} />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        {node.type === "buttons" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Buttons ({(data.buttons || []).length}/3)</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={addButton} data-testid="add-button">
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {(data.buttons || []).map((b, i) => (
              <div key={b.id} className="flex items-center gap-1.5">
                <Input
                  value={b.text}
                  onChange={(e) => updateButton(i, { text: e.target.value })}
                  maxLength={20}
                  className="h-8 text-xs"
                  placeholder="Button label (max 20 chars)"
                  data-testid={`button-text-${i}`}
                />
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => delButton(i)}>
                  <Trash size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* List */}
        {node.type === "list" && (
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">List button label</Label>
              <Input
                value={data.list_title || ""}
                onChange={(e) => setData({ list_title: e.target.value })}
                placeholder="Select One"
                maxLength={20}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Items ({(data.list_items || []).length}/10)</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                onClick={addItem} data-testid="add-list-item">
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {(data.list_items || []).map((it, i) => (
              <div key={it.id} className="space-y-1 border border-[var(--border)] rounded p-1.5">
                <div className="flex items-center gap-1.5">
                  <Input
                    value={it.text}
                    onChange={(e) => updateItem(i, { text: e.target.value })}
                    maxLength={24}
                    className="h-7 text-xs"
                    placeholder="Title (max 24)"
                  />
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => delItem(i)}>
                    <Trash size={12} />
                  </Button>
                </div>
                <Input
                  value={it.description || ""}
                  onChange={(e) => updateItem(i, { description: e.target.value })}
                  maxLength={72}
                  className="h-7 text-xs"
                  placeholder="Description (optional, max 72)"
                />
              </div>
            ))}
          </div>
        )}

        {/* CTA URL — single click-to-open-website button */}
        {node.type === "cta_url" && (
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Button label</Label>
              <Input
                value={data.cta_label || ""}
                onChange={(e) => setData({ cta_label: e.target.value })}
                maxLength={20}
                className="h-8 text-xs"
                placeholder="e.g. Visit website (max 20)"
                data-testid="cta-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">URL <span className="text-[#EF4444]">*</span></Label>
              <Input
                value={data.cta_url || ""}
                onChange={(e) => setData({ cta_url: e.target.value })}
                className="h-8 text-xs font-mono"
                placeholder="https://rodbez.com/offers/diwali"
                data-testid="cta-url"
              />
              {data.cta_url && !/^https?:\/\//.test(data.cta_url.trim()) && (
                <div className="text-[10px] text-[#DC2626]">
                  Must start with http:// or https://
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Footer (optional)</Label>
              <Input
                value={data.cta_footer || ""}
                onChange={(e) => setData({ cta_footer: e.target.value })}
                maxLength={60}
                className="h-8 text-xs"
                placeholder="Small grey text below body (max 60)"
              />
            </div>
            <div className="text-[10px] text-[var(--muted)] leading-relaxed bg-[#F0F9FF] border border-[#BAE6FD] rounded px-2 py-1.5">
              Customer ke click karte hi link unke browser mein khulega.
              Iss node ke baad bot flow rukta hai (kyunki click ka koi
              callback nahi aata) — agar follow-up message bhejna ho to
              ek <b>Message</b> node usse alag set karein.
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-[var(--border)] flex items-center gap-2">
        <Button
          type="button"
          variant={node.data.is_start ? "default" : "outline"}
          size="sm"
          className={`text-xs gap-1.5 flex-1 ${node.data.is_start ? "bg-[#F59E0B] text-white hover:bg-[#D97706]" : ""}`}
          onClick={onSetStart}
          data-testid="set-start-btn"
        >
          <Star size={12} weight={node.data.is_start ? "fill" : "regular"} />
          {node.data.is_start ? "Start node" : "Set as start"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="text-xs text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEE2E2]"
          data-testid="delete-node-btn"
        >
          <Trash size={12} className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

// ---------- Bot list view ----------
function BotList({ items, onCreate, onOpen, onDelete, onActivate, onDeactivate, refreshing, onRefresh }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="heading text-lg font-semibold">Chat Bots</div>
          <div className="text-xs text-[var(--muted)]">
            Visual flow builder. Multiple template-linked bots can run in parallel; only one default (catch-all) bot can be active at a time.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? <CircleNotch size={14} className="animate-spin" /> : "Refresh"}
          </Button>
          <Button size="sm" onClick={onCreate} className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
            data-testid="bot-new-btn">
            <Plus size={14} weight="bold" /> New Bot
          </Button>
        </div>
      </div>

      {items.some((b) => b.is_active) && (
        <Button
          variant="outline" size="sm" onClick={onDeactivate}
          className="gap-1.5 text-[#92400E] border-[#FBBF24] hover:bg-[#FEF3C7]"
          data-testid="deactivate-all-btn"
        >
          <Power size={14} /> Deactivate all bots
        </Button>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] rounded-lg p-10 text-center text-sm text-[var(--muted)]">
          <Robot size={32} className="mx-auto mb-3 opacity-40" />
          No bots yet. Click <b>New Bot</b> to design your first flow.
        </div>
      ) : (
        <ul className="space-y-2 max-h-[55vh] overflow-y-auto scroll-thin pr-1">
          {items.map((b) => (
            <li
              key={b.id}
              className="border border-[var(--border)] rounded-lg p-3 hover:bg-[var(--bg)] cursor-pointer"
              onClick={() => onOpen(b.id)}
              data-testid={`bot-row-${b.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{b.name}</span>
                    {b.is_active && (
                      <Badge className="bg-[#D1FAE5] text-[#065F46] hover:bg-[#D1FAE5] text-[10px]">
                        <CheckCircle size={10} weight="fill" className="mr-1" /> ACTIVE
                      </Badge>
                    )}
                    {b.trigger_template && (
                      <Badge className="bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7] text-[10px] gap-0.5">
                        <Star size={10} weight="fill" /> {b.trigger_template}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5">
                    {b.nodes?.length || 0} nodes · {b.edges?.length || 0} edges
                    {b.trigger_template && ` · ${b.trigger_window_hours}h window`}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost" size="icon" className="h-7 w-7 text-[#991B1B]"
                  onClick={(e) => { e.stopPropagation(); onDelete(b.id, b.name); }}
                  data-testid={`bot-delete-${b.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Bot editor (canvas) ----------
function BotEditor({ botId, onBack, onSaved, fullPage = false }) {
  const [bot, setBot] = useState(null);
  const [name, setName] = useState("");
  const [triggerTemplate, setTriggerTemplate] = useState("");
  const [triggerWindowHours, setTriggerWindowHours] = useState(168);
  const [triggerButtonRoutes, setTriggerButtonRoutes] = useState({});
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [startNodeId, setStartNodeId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (!botId) return;
    api.get(`/bots/${botId}`).then((r) => {
      const b = r.data;
      setBot(b);
      setName(b.name);
      setTriggerTemplate(b.trigger_template || "");
      setTriggerWindowHours(b.trigger_window_hours || 168);
      setTriggerButtonRoutes(b.trigger_button_routes || {});
      setStartNodeId(b.start_node_id);
      setNodes((b.nodes || []).map((n) => ({
        id: n.id, type: n.type, position: n.position || { x: 100, y: 100 },
        data: { ...n.data, type: n.type, is_start: n.id === b.start_node_id },
      })));
      setEdges((b.edges || []).map((e) => ({
        id: e.id, source: e.source, sourceHandle: e.sourceHandle, target: e.target,
      })));
    }).catch(() => toast.error("Failed to load bot"));
    // Load approved Meta templates for the trigger dropdown (with full components)
    api.get("/whatsapp/templates").then((r) => {
      const items = (r.data?.templates || []).filter((t) => t.status === "APPROVED");
      const seen = new Set();
      const unique = items.filter((t) => {
        if (seen.has(t.name)) return false;
        seen.add(t.name);
        return true;
      });
      setApprovedTemplates(unique);
    }).catch(() => setApprovedTemplates([]));
  }, [botId]);

  // Resolve the selected template object for preview
  const selectedTemplate = approvedTemplates.find((t) => t.name === triggerTemplate);
  const templateBody = (selectedTemplate?.components || []).find((c) => c.type === "BODY")?.text || "";
  const templateHeader = (() => {
    const h = (selectedTemplate?.components || []).find((c) => c.type === "HEADER");
    if (!h) return "";
    if (h.format === "TEXT") return h.text || "";
    if (h.format) return `[${h.format}]`;
    return "";
  })();
  const templateButtons = (() => {
    const b = (selectedTemplate?.components || []).find((c) => c.type === "BUTTONS");
    return (b?.buttons || []).filter((btn) => btn.type === "QUICK_REPLY");
  })();

  const [triggerExpanded, setTriggerExpanded] = useState(false);
  const TPL_NODE_ID = "__tpl_trigger__";
  const mappedCount = useMemo(
    () => Object.values(triggerButtonRoutes || {}).filter(Boolean).length,
    [triggerButtonRoutes],
  );

  // Sync is_start flag in node.data when startNodeId changes
  useEffect(() => {
    setNodes((nds) => nds.map((n) => (
      n.id === TPL_NODE_ID ? n : { ...n, data: { ...n.data, is_start: n.id === startNodeId } }
    )));
  }, [startNodeId]);

  // Compute the virtual Template Trigger node. Stored in state so ReactFlow
  // mounts it BEFORE the synthetic edges (separate state update below).
  const tplVirtualNode = useMemo(() => {
    if (!selectedTemplate) return null;
    // Pre-set both width AND height so ReactFlow treats the node as
    // already-measured and does NOT keep it visibility:hidden waiting on
    // its internal ResizeObserver round-trip. Height ≈ header(36) + padding +
    // body(~80) + each button row(28). Generous so handles fit comfortably.
    const estimatedHeight = 100 + Math.max(0, templateButtons.length) * 32;
    return {
      id: TPL_NODE_ID,
      type: "template_trigger",
      position: { x: 30, y: 30 },
      width: 280,
      height: estimatedHeight,
      style: { width: 280, height: estimatedHeight },
      draggable: true,
      deletable: false,
      selectable: false,
      data: {
        template_name: triggerTemplate,
        header: templateHeader,
        body: templateBody,
        buttons: templateButtons,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, triggerTemplate, templateHeader, templateBody, templateButtons.length]);

  const displayNodes = useMemo(() => {
    const real = nodes.filter((n) => n.id !== TPL_NODE_ID);
    return tplVirtualNode ? [tplVirtualNode, ...real] : real;
  }, [nodes, tplVirtualNode]);

  // Synthetic edges live in their own state so we can defer the update by a
  // few animation frames, ensuring ReactFlow has registered the virtual node's
  // handles before drawing the edges (otherwise edges referencing un-measured
  // handles are silently dropped by ReactFlow).
  const [virtualEdges, setVirtualEdges] = useState([]);
  useEffect(() => {
    if (!selectedTemplate) {
      setVirtualEdges([]);
      return undefined;
    }
    const synth = Object.entries(triggerButtonRoutes || {})
      .filter(([, target]) => !!target)
      .map(([label, target]) => ({
        id: `tpl_e_${label}`,
        source: TPL_NODE_ID,
        sourceHandle: `tpl::${label}`,
        target,
        animated: true,
        style: { stroke: "#F59E0B", strokeWidth: 2.5 },
      }));
    // Wait several animation frames + a small timeout to ensure ReactFlow has
    // mounted the virtual node and registered its source handles. Empirically
    // 80ms is enough across browsers; cancellable so React doesn't race.
    const t = setTimeout(() => setVirtualEdges(synth), 80);
    return () => clearTimeout(t);
  }, [selectedTemplate, triggerButtonRoutes]);

  const displayEdges = useMemo(() => {
    const real = edges.filter((e) => e.source !== TPL_NODE_ID && !String(e.id).startsWith("tpl_e_"));
    return [...real, ...virtualEdges];
  }, [edges, virtualEdges]);

  const onNodesChange = useCallback((changes) => {
    // Drop changes that target the virtual node (selection toggles only) or
    // attempt to delete it.
    const filtered = changes.filter((ch) => {
      if (ch.id !== TPL_NODE_ID) return true;
      if (ch.type === "remove") {
        toast.error("Template trigger node cannot be deleted from canvas. Clear the trigger template above instead.");
        return false;
      }
      // Ignore selection/dimensions/position changes for virtual node so it stays put.
      return false;
    });
    setNodes((n) => applyNodeChanges(filtered, n));
  }, []);
  const onEdgesChange = useCallback((changes) => {
    // For synthetic edges, intercept remove and update routes; ignore other changes.
    const realChanges = [];
    changes.forEach((ch) => {
      if (typeof ch.id === "string" && ch.id.startsWith("tpl_e_")) {
        if (ch.type === "remove") {
          const label = ch.id.slice("tpl_e_".length);
          setTriggerButtonRoutes((prev) => {
            const next = { ...prev };
            delete next[label];
            return next;
          });
        }
        return;
      }
      realChanges.push(ch);
    });
    if (realChanges.length) setEdges((e) => applyEdgeChanges(realChanges, e));
  }, []);
  const onConnect = useCallback(
    (params) => {
      // If connecting from the virtual template trigger node, store the route
      // (the synthetic edge will appear via displayEdges memo).
      if (params.source === TPL_NODE_ID && params.sourceHandle?.startsWith("tpl::")) {
        const label = params.sourceHandle.slice("tpl::".length);
        if (label && params.target) {
          setTriggerButtonRoutes((prev) => ({ ...prev, [label]: params.target }));
        }
        return;
      }
      setEdges((eds) => addEdge({ ...params, id: uid("e") }, eds));
    },
    [],
  );

  const addNode = (type) => {
    const id = uid(type[0]);
    const baseData =
      type === "buttons" ? { text: "Choose:", buttons: [{ id: uid("btn"), text: "Option 1" }] } :
      type === "list" ? { text: "Pick one:", list_title: "Select One", list_items: [{ id: uid("li"), text: "Item 1" }] } :
      type === "cta_url" ? { text: "Check it out here!", cta_label: "Visit", cta_url: "" } :
      { text: "Hello!" };
    const newNode = {
      id, type, position: { x: 250 + Math.random() * 200, y: 100 + nodes.length * 30 },
      data: { ...baseData, type },
    };
    setNodes((n) => [...n, newNode]);
    setSelectedId(id);
    if (!startNodeId) setStartNodeId(id);
  };

  const updateSelected = (updated) => {
    setNodes((nds) => nds.map((n) => (n.id === updated.id ? { ...n, data: updated.data } : n)));
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this node?")) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedId && e.target !== selectedId));
    if (startNodeId === selectedId) setStartNodeId(null);
    setSelectedId(null);
  };

  const setStart = () => {
    if (!selectedId) return;
    setStartNodeId(selectedId);
    toast.success("Start node updated");
  };

  const save = async (alsoActivate = false) => {
    if (!name.trim()) return toast.error("Please enter a bot name");
    if (nodes.length === 0) return toast.error("Add at least one node");
    if (!startNodeId) return toast.error("Mark a node as start (⭐) before saving");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        nodes: nodes.map((n) => ({
          id: n.id, type: n.type, position: n.position,
          data: Object.fromEntries(Object.entries(n.data || {}).filter(([k]) => k !== "is_start" && k !== "type")),
        })),
        edges: edges.map((e) => ({
          id: e.id, source: e.source, sourceHandle: e.sourceHandle || null, target: e.target,
        })),
        start_node_id: startNodeId,
        trigger_template: triggerTemplate.trim() || null,
        trigger_window_hours: Math.max(1, parseInt(triggerWindowHours, 10) || 168),
        trigger_button_routes: triggerTemplate.trim()
          ? Object.fromEntries(
              Object.entries(triggerButtonRoutes).filter(([k, v]) => k && v),
            )
          : {},
      };
      await api.patch(`/bots/${botId}`, payload);
      toast.success("Bot saved");
      if (alsoActivate) {
        setActivating(true);
        await api.post(`/bots/${botId}/activate`);
        toast.success(
          triggerTemplate
            ? `Bot activated — will run when customer replies to template "${triggerTemplate}"`
            : "Bot activated — all customer replies will now go through this flow"
        );
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
      setActivating(false);
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedId);

  return (
    <div className={fullPage ? "flex-1 flex flex-col p-4 overflow-hidden" : "h-[78vh] flex flex-col"}>
      {/* Header for full-page mode */}
      {fullPage && (
        <div className="flex items-center gap-2 pb-3 mb-1 border-b border-[var(--border)]">
          <Robot size={20} weight="fill" className="text-[#25D366]" />
          <div className="text-base font-semibold heading">Chat Bot Builder</div>
          <div className="text-xs text-[var(--muted)] ml-2 hidden md:block">
            Design WhatsApp chatbot flows visually. Drag nodes, connect edges, save & activate.
          </div>
        </div>
      )}
      {/* Top bar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} title="Back to bots list">
          <CaretLeft size={18} />
        </Button>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Bot name"
          className="h-9 text-sm max-w-xs font-medium"
          data-testid="bot-name-input"
        />
        <div className="flex items-center gap-1.5 ml-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("message")} data-testid="add-message-node">
            <ChatCircle size={12} weight="fill" className="text-[#10B981]" /> + Message
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("buttons")} data-testid="add-buttons-node">
            <CheckSquare size={12} weight="fill" className="text-[#3B82F6]" /> + Buttons
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("list")} data-testid="add-list-node">
            <ListBullets size={12} weight="fill" className="text-[#8B5CF6]" /> + List
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => addNode("cta_url")} data-testid="add-cta-url-node">
            <LinkSimple size={12} weight="bold" className="text-[#0EA5E9]" /> + Link Button
          </Button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {bot?.is_active && (
            <Badge className="bg-[#D1FAE5] text-[#065F46] hover:bg-[#D1FAE5] text-[10px]">
              <CheckCircle size={10} weight="fill" className="mr-1" /> ACTIVE
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving} data-testid="bot-save-btn">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : "Save"}
          </Button>
          <Button
            size="sm"
            onClick={() => save(true)}
            disabled={saving || activating}
            className="bg-[#25D366] hover:bg-[#1da851] text-white gap-1.5"
            data-testid="bot-save-activate-btn"
          >
            <Power size={12} weight="fill" />
            Save & Activate
          </Button>
        </div>
      </div>

      {/* Trigger Template strip */}
      <div className="mb-2 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg)] flex items-center gap-3 flex-wrap">
        <Star size={14} weight="fill" className="text-[#FBBF24] shrink-0" />
        <div className="text-xs text-[var(--muted)] shrink-0">Trigger:</div>
        <select
          value={triggerTemplate}
          onChange={(e) => setTriggerTemplate(e.target.value)}
          className="h-8 px-2 text-xs rounded-md border border-[var(--border)] bg-[var(--bg)] min-w-[200px]"
          data-testid="bot-trigger-template-select"
        >
          <option value="">— None (default catch-all bot) —</option>
          {approvedTemplates.map((t) => (
            <option key={t.name} value={t.name}>{t.name}</option>
          ))}
        </select>
        {triggerTemplate && (
          <>
            <div className="text-xs text-[var(--muted)] shrink-0">Window:</div>
            <Input
              type="number"
              min="1"
              max="720"
              value={triggerWindowHours}
              onChange={(e) => setTriggerWindowHours(e.target.value)}
              className="h-8 w-20 text-xs"
              data-testid="bot-trigger-window-input"
            />
            <div className="text-xs text-[var(--muted)]">hours</div>
          </>
        )}
        <div className="text-[10px] text-[var(--muted)] flex-1 min-w-[200px] text-right">
          {triggerTemplate
            ? "Bot starts when customer replies to this template (within window)."
            : "No trigger — only runs when activated as default bot."}
        </div>
      </div>

      {/* Template preview + per-button routing (collapsible) */}
      {triggerTemplate && selectedTemplate && (
        <div className="mb-2 rounded-md border border-[#FBBF24]/40 bg-[#FFFBEB] dark:bg-[#FEF3C7]/10">
          <button
            type="button"
            onClick={() => setTriggerExpanded((v) => !v)}
            className="w-full px-3 py-1.5 flex items-center justify-between gap-2 text-left hover:bg-[#FEF3C7]/40 transition-colors"
            data-testid="template-preview-toggle"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Star size={11} weight="fill" className="text-[#92400E] dark:text-[#FBBF24] shrink-0" />
              <span className="text-[11px] uppercase tracking-wide font-semibold text-[#92400E] dark:text-[#FBBF24] truncate">
                Template Preview · {triggerTemplate}
              </span>
              {templateButtons.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FBBF24]/20 text-[#92400E] dark:text-[#FBBF24] shrink-0">
                  {mappedCount}/{templateButtons.length} mapped
                </span>
              )}
            </div>
            <CaretLeft
              size={14}
              className={`transition-transform shrink-0 text-[#92400E] dark:text-[#FBBF24] ${triggerExpanded ? "-rotate-90" : "rotate-180"}`}
            />
          </button>
          {triggerExpanded && (
          <div className="px-3 pb-2.5">
          {templateHeader && (
            <div className="text-xs font-semibold text-[#1F2937] dark:text-white mb-1">{templateHeader}</div>
          )}
          {templateBody && (
            <div className="text-xs text-[#1F2937] dark:text-[#E5E7EB] whitespace-pre-wrap mb-2 leading-relaxed bg-white dark:bg-[var(--card)] rounded p-2 border border-[var(--border)]">
              {templateBody}
            </div>
          )}
          {templateButtons.length > 0 ? (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                Button routes — pick which node fires when each button is clicked
              </div>
              {nodes.length === 0 && (
                <div className="text-[11px] text-[#92400E] bg-[#FEF3C7] dark:bg-[#FEF3C7]/20 rounded px-2 py-1.5 border border-[#FBBF24]/40">
                  ⚠️ Pehle <b>+ Message / + Buttons / + List</b> button se ek-do nodes canvas par add karein. Phir yahaan dropdown mein woh nodes dikhenge taaki button → node link kar sakein.
                </div>
              )}
              {templateButtons.map((btn) => {
                const label = btn.text;
                const target = triggerButtonRoutes[label] || "";
                return (
                  <div key={label} className="flex items-center gap-2">
                    <div className="px-2 py-1 rounded bg-[#25D366]/10 text-[#1da851] text-xs font-medium min-w-[120px] text-center border border-[#25D366]/30">
                      {label}
                    </div>
                    <div className="text-xs text-[var(--muted)]">→</div>
                    <select
                      value={target}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTriggerButtonRoutes((prev) => {
                          const next = { ...prev };
                          if (v) next[label] = v;
                          else delete next[label];
                          return next;
                        });
                      }}
                      className="h-7 px-2 text-xs rounded-md border border-[var(--border)] bg-[var(--bg)] flex-1 min-w-[200px]"
                      data-testid={`btn-route-${label}`}
                    >
                      <option value="">— Use default start node —</option>
                      {nodes.map((n) => {
                        const preview = (n.data?.text || "").slice(0, 50);
                        const star = n.id === startNodeId ? "⭐ " : "";
                        const label = `${star}[${n.type}] ${preview || n.id}`;
                        return (
                          <option key={n.id} value={n.id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                );
              })}
              <div className="text-[10px] text-[var(--muted)] mt-1">
                💡 No mapping = bot starts at the default ⭐ start node when this button is clicked.
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-[var(--muted)] italic">
              This template has no quick-reply buttons. Bot will always start at default ⭐ node.
            </div>
          )}
          </div>
          )}
        </div>
      )}

      {/* Canvas + side panel */}
      <div className="flex-1 grid grid-cols-[1fr_320px] gap-3 min-h-0">
        <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[#FAFAFA]">
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
          >
            <Background gap={20} color="#E5E7EB" />
            <Controls />
            <MiniMap zoomable pannable />
          </ReactFlow>
        </div>
        <div className="border border-[var(--border)] rounded-lg bg-[var(--surface)] overflow-hidden">
          {selectedNode ? (
            <NodeEditor
              node={selectedNode}
              onChange={updateSelected}
              onDelete={deleteSelected}
              onSetStart={setStart}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center text-xs text-[var(--muted)]">
              <Robot size={28} className="opacity-40 mb-2" />
              <div className="font-medium mb-1">Select a node to edit</div>
              <div>Click any node on the canvas. Or add a new node from top toolbar.</div>
              <div className="mt-3 text-[10px]">⭐ marks the start node — first message customer receives.</div>
              <div className="mt-1 text-[10px]">Drag from a button/item handle to another node to connect.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Top-level dialog (list-only; editor opens in a full page) ----------
export default function BotsDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/bots");
      setItems(r.data.items || []);
    } catch (_) {}
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]);

  const openEditor = (id) => {
    onOpenChange(false);
    navigate(`/bots/${id}`);
  };

  const createBot = async () => {
    const name = window.prompt("Bot name?");
    if (!name?.trim()) return;
    try {
      const r = await api.post("/bots", { name: name.trim(), nodes: [], edges: [] });
      openEditor(r.data.id);
    } catch (e) {
      toast.error("Failed to create bot");
    }
  };

  const deleteBot = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Sessions will be cleared.`)) return;
    try {
      await api.delete(`/bots/${id}`);
      toast.success("Bot deleted");
      refresh();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  const deactivateAll = async () => {
    try {
      await api.post("/bots/deactivate");
      toast.success("All bots deactivated");
      refresh();
    } catch (_) {
      toast.error("Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="bots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Robot size={20} weight="fill" className="text-[#25D366]" />
            Chat Bots
          </DialogTitle>
          <DialogDescription>
            Pick a bot to edit it on a full-screen canvas. Click <b>New Bot</b> to start designing.
          </DialogDescription>
        </DialogHeader>
        <BotList
          items={items}
          refreshing={refreshing}
          onRefresh={refresh}
          onCreate={createBot}
          onOpen={openEditor}
          onDelete={deleteBot}
          onDeactivate={deactivateAll}
        />
      </DialogContent>
    </Dialog>
  );
}

// ---------- Full-page editor route component ----------
export function BotEditorPage() {
  const navigate = useNavigate();
  const { botId } = useParams();
  return (
    <div className="h-screen w-screen bg-[var(--bg)] flex flex-col overflow-hidden">
      <ReactFlowProvider>
        <BotEditor
          botId={botId}
          onBack={() => navigate("/")}
          onSaved={() => {}}
          fullPage
        />
      </ReactFlowProvider>
    </div>
  );
}
